import React from "react";
import {Row, Col, Button, Modal, Form} from "react-bootstrap";
import InlineForm from "../FromGroup/InlineForm";
import Component from "../../../components/AscComponent";
import CommonTable from "../Table/CommonTable";
import {faPlus, faMinus, faEdit, faXmark, faLock} from "@fortawesome/free-solid-svg-icons";
import SetDataWithSpeechBubble from "./SetDataWithSpeechBubble";
import ButtonSpeechBubble from "./SetButtonWithSpeechBubble";
import moment from "moment";
import * as GlobalConst from "../../../components/AscConstants";
import "./AccountElements.css";

/**
 * 【#586】表の行を区分ごとにまとめ、区分の先頭へ見出し行を差し込む。
 * 各行の section に区分キーを持たせておき、ここで決めた順に並べ直す。
 * 行が 1 件も無い区分は見出しも出さない。
 * CommonTable は列結合を持たないため、見出しは項目セルへ置き、
 * 行全体の背景は CSS（.account-section-heading を含む行）で付ける。
 */
const ACCOUNT_SECTION_ORDER = ["basic", "security", "sub_user", "recording", "external_number"];

function withAccountSectionHeadings(rows, langText) {
    const result = [];
    ACCOUNT_SECTION_ORDER.forEach(key => {
        const sectionRows = rows.filter(row => row.section === key);
        if (sectionRows.length === 0) return;
        result.push({
            section: key,
            account_info_item: <div className="account-section-heading">{langText.Body.AccountSections[key]}</div>,
            account_info_data: <div className="account-section-heading-spacer" />
        });
        result.push(...sectionRows);
    });
    return result;
}

/**
 * 【#586】入力欄の下に出す誤りの説明。赤枠だけでは何が誤りか分からないため文で出す。
 */
function AccountFieldError({message}) {
    if (!message) return null;
    return <div className="account-field-error">{message}</div>;
}

export class EditAccountInfo extends Component {
    /**
     * サブID の作成上限（Issue #541）
     *
     * 上限は hm15_users.options.sub_user_limit で、/Account/getAccountInfo の応答から
     * state.subUserLimit として渡ってくる。未設定・不正値は既定値（5）へ倒す。
     * セッションの userInfo ではなく取得時の値を使う（管理者が上限を変えた直後でも
     * 再ログインを待たずに反映される）。
     */
    getSubUserLimit() {
        const value = this.props.state.subUserLimit;
        if (!Number.isInteger(value)) return GlobalConst.VALUES.sub_user_limit_default;
        if (value < GlobalConst.VALUES.sub_user_limit_min
            || value > GlobalConst.VALUES.sub_user_limit_max) {
            return GlobalConst.VALUES.sub_user_limit_default;
        }
        return value;
    }

    validateTelNumber(param, dflt = false){
        let validation_flag = dflt;
        if(param){
            validation_flag = super.validateTelNumber(param);
        }
        return validation_flag;
    }

    validatePassword(param, dflt = false){
        let validation_flag = dflt;
        if(param){
            validation_flag = super.validatePassword(param);
        }
        return validation_flag;
    }

    validateMail(param, dflt = false){
        let validation_flag = dflt;
        if(param){
            validation_flag = super.validateMail(param);
        }
        return validation_flag;
    }

    /**
     * 【#586】Account.validationHandle と同じ条件で、各欄の誤りを文で返す。
     * 判定条件そのものは変えない（「確認」を押せる条件は validationHandle が正）。
     */
    getSubUserPasswordError(row) {
        const body = this.props.langText.Body;
        if (row.password) {
            return this.validatePassword(row.password) ? null : body.AccountValidationPasswordPolicy;
        }
        return row.new ? body.AccountValidationPasswordRequired : null;
    }

    getPasswordErrors() {
        const body = this.props.langText.Body;
        const {password, password_new, password_new_confirm} = this.props.state;
        const errors = {password: null, password_new: null, password_new_confirm: null};
        if (password) {
            if (!this.validatePassword(password)) errors.password = body.AccountValidationPasswordPolicy;
        } else if (password_new || this.props.reauthRequired) {
            // 新しいパスワードを入れたとき・メールアドレス／二要素認証を変えたときは現在のパスワードが要る
            errors.password = body.AccountValidationCurrentPasswordRequired;
        }
        if (password_new && !this.validatePassword(password_new)) {
            errors.password_new = body.AccountValidationPasswordPolicy;
        }
        if (password_new && password_new_confirm !== password_new) {
            errors.password_new_confirm = body.AccountValidationPasswordMismatch;
        }
        return errors;
    }

    subUserPlus() {
        let subUsers = this.props.state.subUsers;
        let oldSubUsers = this.props.state.oldSubUsers;
        // 8文字、数字および小英字が1-4入っているパスワードを生成
        let intNum = Math.floor(Math.random() * (3 - 1)) + 1;
        let sNum = Math.floor(Math.random() * (4 - 1)) + 1;
        let S = "abcdefghijklmnopqrstuvwxyz";
        let B = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        let N = 8 - intNum - sNum;
        let tmp = Array.from(crypto.getRandomValues(new Uint8Array(N))).map((n)=>S[n%S.length]).join('');
        tmp += Array.from(crypto.getRandomValues(new Uint8Array(sNum))).map((n)=>B[n%B.length]).join('');
        for (let i = 0; i < intNum; i++) {
            tmp += String(Math.floor(Math.random() * 9));
        }
        let obj = {};
        for (let i = 0; i < tmp.length; i++) {
            // ランダムな数値を生成する ※文字数より少ないと永久ループする
            let rand = Math.floor(Math.random() * 100);
            // 同じキー名がないか判別する
            if (!obj[rand]) {
                // 同じキー名がない場合にobjに追加する
                obj[rand] = tmp[i];
            } else {
                // 同じキー名ある場合、やり直す
                i--;
            }
        }
        let password = Object.values(obj).join('');
        N = 3;
        for (let i = 0; i <= 30; i++) {
            let tmpId = Array.from(crypto.getRandomValues(new Uint8Array(N))).map((n)=>S[n%S.length]).join('');
            if (subUsers.every(item => item.sub_user_id !== this.props.state.user_id + tmpId)
                && oldSubUsers.every(item => item.sub_user_id !== this.props.state.user_id + tmpId)) {
                // 新しく作った分と削除した分で重複しないように
                subUsers.push({sub_user_id: this.props.state.user_id + tmpId, password, memo: "", new: true, edit: true});
                this.props.propSetState({subUsers});
                return;
            } else if (i === 30) {
                return console.error("sub_user_id create error.");
            }
        }
    }

    subUserEdit(e, type, index) {
        let subUsers = this.props.state.subUsers;
        switch (type) {
            case "sub_user_memo":
                subUsers[index].memo = e.target.value;
                break;
            case "sub_user_password":
                subUsers[index].password = e.target.value;
                break;
            default:
                break;
        }
        this.props.propSetState({subUsers});
    }

    subUserMinus(index) {
        let subUsers = this.props.state.subUsers;
        subUsers.splice(index, 1);
        this.props.propSetState({subUsers});
    }

    subUserPasswordEdit(index, flag) {
        let subUsers = this.props.state.subUsers;
        subUsers[index].edit = flag;
        if (!flag) {
            subUsers[index].password = "";
        }
        this.props.propSetState({subUsers});
    }

    subUsersGetEdit() {
        return this.props.state.subUsers.map((row,index) => {
            return <Row className = "sub_user_row" key={`sub_user_row-${index}`}>
                <ButtonSpeechBubble
                    key = {`sub_user_minus-button-${index}`}
                    size="sm"
                    className="sub_user_minus-button"
                    DisplayIcon={faMinus}
                    speechBubble={this.props.langText.Body.AccountSubUserMinus}
                    onClick={() => this.subUserMinus(index)}
                />
                <Col className = "sub_user">
                    <InlineForm
                        key = {`sub_user-${index}`}
                        controlId = {`sub_user-${index}`}
                        value = {row.sub_user_id}
                        readOnly
                    />
                </Col>
                <Col className = "sub_user_memo">
                    <InlineForm
                        key = {`sub_user_memo-${index}`}
                        controlId = {`sub_user_memo-${index}`}
                        type = "text"
                        value = {row.memo}
                        onChange = {e => this.subUserEdit(e, "sub_user_memo", index)}
                    />
                </Col>
                {this.subUserMfaCols(row)}
                <Col className = "sub_user_password">
                    {row.edit ? !row.new ? <div className="sub_user_password_change">
                                <ButtonSpeechBubble
                                    key = {`sub_user_password_change_cancel-${index}`}
                                    size="sm"
                                    className="sub_user_minus-button"
                                    DisplayIcon={faXmark}
                                    speechBubble={this.props.langText.Body.PasswordChangeCancel}
                                    onClick={() => this.subUserPasswordEdit(index, false)}
                                />
                                <InlineForm
                                    key = {`sub_user_password-${index}`}
                                    controlId = {`sub_user_password-${index}`}
                                    type = "text"
                                    value = {row.password}
                                    placeholder = {this.props.langText.Body.PasswordPlaceholder}
                                    isInvalid = {(!row.new && !this.validatePassword(this.props.state.subUsers[index].password,true))
                                        || (row.new && !this.validatePassword(this.props.state.subUsers[index].password,false)) }
                                    onChange = {e => this.subUserEdit(e, "sub_user_password", index)}/>
                                <AccountFieldError message={this.getSubUserPasswordError(row)} />
                            </div> :
                            <>
                            <InlineForm
                                key = {`sub_user_password-${index}`}
                                controlId = {`sub_user_password-${index}`}
                                type = "text"
                                value = {row.password}
                                placeholder = {this.props.langText.Body.PasswordPlaceholder}
                                isInvalid = {(!row.new && !this.validatePassword(this.props.state.subUsers[index].password,true))
                                    || (row.new && !this.validatePassword(this.props.state.subUsers[index].password,false)) }
                                onChange = {e => this.subUserEdit(e, "sub_user_password", index)}/>
                            <AccountFieldError message={this.getSubUserPasswordError(row)} />
                            </>
                        : <ButtonSpeechBubble
                            key = {"sub_user_password_edit"}
                            size="sm"
                            className="sub_user_plus-button"
                            DisplayIcon={faEdit}
                            speechBubble={this.props.langText.Body.AccountInfoList.password_change}
                            onClick={() => this.subUserPasswordEdit(index, true)}
                        />
                    }
                </Col>
                {!row.new && (
                    <ButtonSpeechBubble
                        key={`sub_user_permission-button-${index}`}
                        size="sm"
                        className="sub_user_plus-button"
                        DisplayIcon={faLock}
                        speechBubble={this.props.langText.Body.AccountSubUserPermission}
                        onClick={() => this.props.onPermissionEdit(index)}
                    />
                )}
            </Row>
        });
    }


    /**
     * サブユーザー行の二要素認証の列（Issue #443）
     * 送信先メールアドレスと有効/無効を、既存のサブユーザー欄の右へ並べる。
     * 別ブロックに分けると同じサブユーザーの設定が画面の2箇所へ散るため。
     *
     * 列幅は既存の列と同じく自動分配に任せる（md を指定すると合計が 12 を超えて
     * 削除ボタン・権限ボタンの分だけ行が折り返す）。
     *
     * 未登録のサブユーザーは hm16 のレコードがまだ無いので設定できない。
     */
    subUserMfaCols(row) {
        // サブユーザー自身のセッションでは兄弟サブユーザーの二要素認証を設定できない
        // （サーバも sub_users 付きの要求を 403 で弾く）。main では
        // サブユーザーの二要素認証ブロック全体を !isSubUserSession で隠していたので、
        // 列へ統合したこちらでも同じ条件で出さない。
        // 出してしまうと mfaSubUsers が常に空になるため、登録済みの行にも
        // 「登録後に設定できます」が並んで実態と食い違う。
        if (this.props.isSubUserSession) return null;

        const mfaSubUsers = this.props.state.mfaSubUsers || [];
        const mfaIndex = mfaSubUsers.findIndex(item => item.sub_user_id === row.sub_user_id);

        if (row.new || mfaIndex < 0) {
            return (
                <>
                    <Col className="sub_user_mfa_mail text-muted">
                        {this.props.langText.Body.MfaSettingSubUserAfterSave}
                    </Col>
                    <Col className="sub_user_mfa_switch" />
                </>
            );
        }

        const mfaRow = mfaSubUsers[mfaIndex];
        const invalidMail = !!mfaRow.mail_address && !this.validateMail(mfaRow.mail_address);

        return (
            <>
                <Col className="sub_user_mfa_mail">
                    <Form.Control
                        id = {`mfa-sub-user-mail-${mfaRow.hm16_id}`}
                        type = "email"
                        size = "sm"
                        value = {mfaRow.mail_address || ""}
                        maxLength = {128}
                        isInvalid = {invalidMail}
                        placeholder = {this.props.langText.Body.MfaSettingSubUserMailPlaceholder}
                        onChange = {e => this.props.onChangeMfaSubUser(mfaIndex, 'mail_address', e.target.value)}
                    />
                    {invalidMail && (
                        <Form.Control.Feedback type="invalid">
                            {this.props.langText.Body.MfaSettingSubUserMailInvalid}
                        </Form.Control.Feedback>
                    )}
                </Col>
                <Col className="sub_user_mfa_switch">
                    <Form.Check
                        type = "switch"
                        id = {`mfa-sub-user-enabled-${mfaRow.hm16_id}`}
                        checked = {!!mfaRow.mfa_enabled}
                        onChange = {e => this.props.onChangeMfaSubUser(mfaIndex, 'mfa_enabled', e.target.checked)}
                    />
                </Col>
            </>
        );
    }

    render() {
        let columns = [
            {
                Header: this.props.langText.Body.Item,
                accessor: 'account_info_item',
                width: this.props.boardWidth.xsmall
            }, {
                Header: this.props.langText.Body.Information,
                accessor: 'account_info_data',
                width: this.props.boardWidth.xlarge
            }
        ]
        let returnValue =[];

        returnValue.push({
            section: "basic",
            account_info_item: this.props.langText.Body.AccountInfoList.user_id,
            account_info_data: <InlineForm
                key="user_id"
                controlId="user_id"
                readOnly
                children={this.props.state.user_id}
                value = {this.props.state.user_id}
            />,
        });
        returnValue.push({
            section: "basic",
            account_info_item: this.props.langText.Body.AccountInfoList.name,
            account_info_data: <InlineForm
                key="user_id"
                controlId="user_id"
                readOnly
            >
                <Row>
                    <Col className = "user_name">
                        <InlineForm
                            key = "family_name"
                            controlId = "family_name"
                            label = {this.props.langText.Body.FamilyName}
                            type = "text"
                            value = {this.props.state.family_name}
                            onChange = {e => this.props.onTextChange(e, 'family_name')}
                        />
                    </Col>
                    <Col className = "user_name">
                        <InlineForm
                            key = "first_name"
                            controlId = "first_name"
                            label = {this.props.langText.Body.FirstName}
                            type = "text"
                            value = {this.props.state.first_name}
                            onChange = {e => this.props.onTextChange(e, 'first_name')}
                        />
                    </Col>
                </Row>
            </InlineForm>
        });
        // #443 でメールアドレスの変更に現在のパスワードの再入力が必須になった
        // （server /Account/update と Account.validationHandle が同じ条件で判定する）。
        // その入力欄は「パスワード変更」を開くまで描画されないため、メールアドレスだけを
        // 直した利用者には「確認ボタンが押せない」状態にしか見えていなかった。
        // 変更を検知した時点で、理由の説明と現在のパスワード欄をこの行に出す。
        const mailChanged = this.props.state.mail_address !== this.props.state.initial_mail_address;
        const passwordErrors = this.getPasswordErrors();
        returnValue.push({
            section: "basic",
            // 【#586】必須項目には他画面（InlineForm の required）と同じ「（*）」を付ける。
            // 変更不可のサブユーザーセッションでは付けない
            account_info_item: this.props.isSubUserSession
                ? this.props.langText.Body.AccountInfoList.mail_address
                : <>{this.props.langText.Body.AccountInfoList.mail_address}<span className="account-required-mark">（*）</span></>,
            account_info_data: this.props.isSubUserSession
                // サブユーザー経路のメールアドレス変更はサーバ側が 403 で拒否する
                // （変更されるのは親ユーザーのメール＝親のOTPの宛先になるため）。
                // 入力させた末にエラーで返すのを避け、画面側でも変更不可にしておく
                ? <div className = "account-mail-address">
                    <InlineForm
                        key = "mail_address"
                        type = "mail"
                        value = {this.props.state.mail_address}
                        readOnly
                    />
                    <div className = "account-mail-note">
                        {this.props.langText.Body.AccountMailChangeSubUserNote}
                    </div>
                </div>
                : <div className = "account-mail-address">
                    <InlineForm
                        key = "mail_address"
                        type = "mail"
                        value = {this.props.state.mail_address}
                        onChange = {e => this.props.onTextChange(e, 'mail_address')}
                        isInvalid = {!this.validateMail(this.props.state.mail_address)}
                    />
                    <AccountFieldError message={!this.props.state.mail_address
                        ? this.props.langText.Body.EmailAddressRequired
                        : !this.validateMail(this.props.state.mail_address)
                            ? this.props.langText.Body.AccountValidationMailInvalid : null} />
                    {mailChanged &&
                    <div className = "account-mail-reauth">
                        <div className = "account-mail-note account-mail-note-required">
                            {this.props.langText.Body.AccountMailChangeReauthNote}
                        </div>
                        {this.props.state.mfaEnabled &&
                        <div className = "account-mail-note">
                            {this.props.langText.Body.AccountMailChangeMfaNote}
                        </div>
                        }
                        {/* ここは「なぜ現在のパスワードが要るのか」を編集中に伝える注記だけを置く。
                            入力欄は「設定」を押したときの ReauthPasswordModal で聞く。
                            表や行へ欄を置くと編集画面にしか無いことになり、
                            確認画面で再認証が失敗したときに直す先が見えなくなる。 */}
                    </div>
                    }
                </div>
        });
        returnValue.push({
            section: "basic",
            account_info_item: this.props.langText.Body.AccountInfoList.tel_number,
            account_info_data: <div className="account-tel-number">
                <InlineForm
                    key = "tel_number"
                    type = "text"
                    value = {this.props.state.tel_number}
                    onChange = {e => this.props.onTextChange(e, 'tel_number')}
                    isInvalid = {!this.validateTelNumber(this.props.state.tel_number,true)}
                />
                <AccountFieldError message={this.validateTelNumber(this.props.state.tel_number,true)
                    ? null : this.props.langText.Body.AccountValidationTelInvalid} />
            </div>
        });
        returnValue.push({
            section: "security",
            account_info_item: this.props.langText.Body.AccountInfoList.password_change,
            account_info_data: this.props.state.password_change ?
                <div>
                    <Row className = "sub_user_row">
                        <Col className = "password">
                            <InlineForm
                                key = "password"
                                controlId = "password"
                                type = "password"
                                value = {this.props.state.password}
                                onChange = {e => this.props.onTextChange(e, "password")}
                                label = {this.props.langText.Body.Password}
                                isInvalid = {!this.validatePassword(this.props.state.password,true)
                                || this.props.state.password_new? !this.validatePassword(this.props.state.password): false}
                            />
                            <AccountFieldError message={passwordErrors.password} />
                        </Col>
                        <Col className = "password">
                            <InlineForm
                                key = "password_new"
                                controlId = "password_new"
                                type = "password"
                                value = {this.props.state.password_new}
                                onChange = {e => this.props.onTextChange(e, "password_new")}
                                label = {this.props.langText.Body.PasswordNew}
                                isInvalid = {!this.validatePassword(this.props.state.password_new,true)}
                            />
                            <AccountFieldError message={passwordErrors.password_new} />
                        </Col>
                        <Col className = "password">
                            <InlineForm
                                key = "password_new_confirm"
                                controlId = "password_new_confirm"
                                type = "password"
                                value = {this.props.state.password_new_confirm}
                                onChange = {e => this.props.onTextChange(e, "password_new_confirm")}
                                label = {this.props.langText.Body.PasswordNewRe}
                                isInvalid = {!this.validatePassword(this.props.state.password_new_confirm,true)
                                    || this.props.state.password_new !== this.props.state.password_new_confirm}
                            />
                            <AccountFieldError message={passwordErrors.password_new_confirm} />
                        </Col>

                    </Row>
                    <ButtonSpeechBubble
                        key = {"password_changed"}
                        size="sm"
                        className="sub_user_minus-button"
                        DisplayIcon={faXmark}
                        speechBubble={this.props.langText.Body.PasswordChangeCancel}
                        onClick={() => this.props.propSetState({
                            password_change: false,
                            password_new_confirm: null,
                            password_new: null,
                            // 再認証の現在パスワードは本人確認モーダル（reauthPassword）で受け取るため、
                            // 引き継ぎ先の「現在のパスワード」行は既に無い。残すと入力欄が画面に無いまま
                            // validationHandle が false になり、直す手段が無いまま「確認」が押せなくなる
                            password: null
                        })}
                    />
                </div>
                : <ButtonSpeechBubble
                    key = {"password_changed"}
                    size="sm"
                    className="sub_user_plus-button"
                    DisplayIcon={faEdit}
                    speechBubble={this.props.langText.Body.AccountInfoList.password_change}
                    onClick={() => this.props.propSetState({password_change: true})}
                />

        });
        returnValue.push({
            section: "sub_user",
            account_info_item: <SetDataWithSpeechBubble
                displayData = {this.props.langText.Body.AccountInfoList.sub_users}
                speechBubbleData = {this.props.langText.Body.AccountSubUserExp
                    .replace("{n}", this.getSubUserLimit())}
                infoIconDisplayFlag = {true}
                infoIconType = {null}
                infoIconClassName = "modal-info-icon"
                infoIconColor = "silver"
                infoIconSize = "lg"/>,
            account_info_data: <InlineForm
                key="sub_user_id"
                controlId="sub_user_id"
                readOnly
            >
                {/* 【#586】上限に達すると「＋」が消えるため、件数と上限を常に出しておく。
                    上限は「＋」の表示判定と同じ getSubUserLimit() を使う */}
                <div className="account-sub-user-count">
                    {this.props.langText.Body.AccountSubUserCount
                        .replace("{0}", this.props.state.subUsers.length)
                        .replace("{1}", this.getSubUserLimit())}
                    {this.props.state.subUsers.length >= this.getSubUserLimit() &&
                        <span className="account-sub-user-limit-note">{this.props.langText.Body.AccountSubUserLimitReached}</span>}
                </div>
                {this.props.state.subUsers.length > 0 ? <Row className="sub_user_row_head">
                    <div className="sub_user_minus-button-space">
                    </div>
                    <Col className="sub_user">
                        {this.props.langText.Body.AccountSubUserId}
                    </Col>
                    <Col className="sub_user_memo">
                        {this.props.langText.Body.AccountMemo}
                    </Col>
                    {/* 行側（subUserMfaCols）と出す条件を揃える */}
                    {!this.props.isSubUserSession && <>
                        <Col className="sub_user_mfa_mail">
                            {this.props.langText.Body.MfaSettingSubUserMail}
                        </Col>
                        <Col className="sub_user_mfa_switch">
                            {this.props.langText.Body.MfaStatus}
                        </Col>
                    </>}
                    <Col className="sub_user_password">
                        {this.props.langText.Body.Password}
                    </Col>
                    {/* 権限ボタン（列外の固定幅）の分だけ、見出しにも同じ幅の余白を置く */}
                    <div className="sub_user_minus-button-space">
                    </div>
                </Row> : null}
                {this.subUsersGetEdit()}
                {/* 送信先メールを空にしたときの挙動は分かりづらいので明記する。
                    列そのものを出さないサブユーザーセッションでは注記も出さない */}
                {!this.props.isSubUserSession && this.props.state.subUsers.length > 0 && (
                    <p className="sub_user_mfa_note">{this.props.langText.Body.MfaSettingSubUserMailUnset}</p>
                )}
                {/* 【#541】上限はユーザーごとの設定（既定 5 件）。上限に達したら「＋」を出さない。
                    上限を後から下げて既存件数が上限を超えている場合も、エラーにはせず
                    「＋」を隠すだけにする（既存のサブユーザーは削除しない）。 */}
                {this.props.state.subUsers.length >= this.getSubUserLimit() ? null : <Row>
                    <ButtonSpeechBubble
                        key = {"sub_user"}
                        size="sm"
                        className="sub_user_plus-button"
                        DisplayIcon={faPlus}
                        speechBubble={this.props.langText.Body.AccountSubUserPlus}
                        onClick={() => this.subUserPlus(this.props.state.subUsers.length)}
                    />
                </Row>}
            </InlineForm>
        });
        let recorded_time_arr = [];
        for (let i = 0; i < 3; i++) {
            let l = this.props.state.ht60_total.find(row => row.month === moment().utc().add(9, "h").add(-i, "M").format("YYYY-MM"));
            if (l) {
                recorded_time_arr.push(this.getFormatSec(l.sum));
            } else {
                recorded_time_arr.push(this.getFormatSec(0));
            }
        }
        returnValue.push({
            section: "recording",
            account_info_item: this.props.langText.Body.AccountInfoList.recorded_time,
            account_info_data: <InlineForm
                key="recorded_time_3month"
                controlId="recorded_time_3month"
                readOnly
            >
                <Row className="recorded_time_3month_row_head">
                    <Col className="recorded_time_3month_2month_ago">
                        <InlineForm
                            key="2month_ago"
                            controlId="2month_ago"
                            readOnly
                            label = {this.props.langText.Body.AccountRecordedTime2monthAgo}
                            children={recorded_time_arr[2]}
                        />
                    </Col>
                    <Col className="recorded_time_3month_1month_ago">
                        <InlineForm
                            key="1month_ago"
                            controlId="1month_ago"
                            readOnly
                            label = {this.props.langText.Body.AccountRecordedTime1monthAgo}
                            children={recorded_time_arr[1]}
                        />
                    </Col>
                    <Col className="recorded_time_3month_this_month">
                        <InlineForm
                            key="this_month"
                            controlId="this_month"
                            readOnly
                            label = {this.props.langText.Body.AccountRecordedTimeThisMonth}
                            children={recorded_time_arr[0]}
                        />
                    </Col>
                </Row>
            </InlineForm>,
        });

        // 録音保存期限の表示
        if (this.props.state.voice_expire_display && this.props.state.voice_expire_display.show) {
            const voiceExpireData = this.props.state.voice_expire_display;
            const expireText = voiceExpireData.option_years > 0
                ? `${voiceExpireData.default_years} ${this.props.langText.Body.Year} + ${voiceExpireData.option_years} ${this.props.langText.Body.Year}`
                : `${voiceExpireData.default_years}  ${this.props.langText.Body.Year}`;

            returnValue.push({
                section: "recording",
                account_info_item: this.props.langText.Body.AccountInfoList.voice_expire,
                account_info_data: <InlineForm
                    key="voice_expire"
                    controlId="voice_expire"
                    readOnly
                    children={expireText}
                />
            });
        }

        // 再認証（メールアドレス・二要素認証の変更）で要る現在のパスワードは、
        // 表に欄を足さず「設定」を押したときにモーダルで聞く（ReauthPasswordModal）。
        // 表へ置くと、入力欄が編集画面にしか無いために
        // 確認画面で再認証が失敗したときに直す先が見えなくなる。
        // 「パスワード変更」を開いているときはそのブロックの
        // 「現在のパスワード」をそのまま使うため、モーダルは出さない。

        // 外線番号の名称（Issue #289）
        // 番号の数だけ行が伸びて1画面に収まらなくなるため、この行にはボタンだけを置き、
        // 実際の編集はモーダルで行う。保存は画面下部の「確認」に一本化しているので
        // モーダル側には保存ボタンを置かない（親の state を書き換えるだけ）。
        if ((this.props.externalNumbers || []).length > 0) {
            const namedCount = this.props.externalNumbers
                .filter(row => (this.props.state.externalNumberDescriptions[row.external_number] || "") !== "").length;
            returnValue.push({
                section: "external_number",
                account_info_item: this.props.langText.Body.AccountExternalNumberDescriptionTitle,
                account_info_data: <div key="external-number-summary" className="external-number-summary">
                    <span className="external-number-summary-text">
                        {this.props.langText.Body.AccountExternalNumberDescriptionSummary
                            .replace('{0}', this.props.externalNumbers.length)
                            .replace('{1}', namedCount)}
                    </span>
                    <Button
                        variant="default"
                        size="sm"
                        className="table-button"
                        onClick={() => this.props.onOpenExternalNumberModal()}
                    >
                        {this.props.langText.Body.Set}
                    </Button>
                </div>
            });
        }

        // 二要素認証（Issue #443）
        // 本人の設定だけを置く。サブユーザーの分はサブユーザー欄の右へ並べており、
        // 同じサブユーザーの設定が画面の2箇所に散らないようにしている。
        // 【#586】区分「ログイン・セキュリティ」に属し、パスワード変更の直後に並ぶ。
        returnValue.push({
            section: "security",
            account_info_item: this.props.langText.Body.MfaStatus,
            account_info_data: (
                // 【#586】トグルだけだと状態と効果が伝わりにくいため、状態の表示と説明を添えたカードにする。
                // 入力（id・checked・onChange）は従来どおり。
                <div className={`account-mfa-card${this.props.state.mfaEnabled ? " is-on" : ""}`} key="mfa-enabled-cell">
                    <div className="account-mfa-main">
                        <Form.Check
                            type = "switch"
                            id = "mfa-enabled"
                            className = "account-mfa-switch"
                            label = {this.props.langText.Body.MfaSettingEnable}
                            checked = {!!this.props.state.mfaEnabled}
                            onChange = {e => this.props.onChangeMfaEnabled(e.target.checked)}
                        />
                        <span className={`account-mfa-status${this.props.state.mfaEnabled ? " is-on" : ""}`}>
                            {this.props.state.mfaEnabled
                                ? this.props.langText.Body.MfaStatusOn
                                : this.props.langText.Body.MfaStatusOff}
                        </span>
                    </div>
                    <div className="account-mfa-desc">
                        {this.props.state.mfaEnabled
                            // 送信先は保存済みのアドレス。入力中の新しいアドレスは「設定」するまで使われない。
                            // サブユーザーは自身の送信先を優先し、未設定なら親ユーザーのアドレス（サーバーの resolveMfaTarget と同じ順）
                            ? this.props.langText.Body.AccountMfaDescOn.replace("{0}", this.props.state.ownMfaMailAddress || this.props.state.initial_mail_address || "")
                            : this.props.langText.Body.AccountMfaDescOff}
                    </div>
                </div>
            )
        });

        return (
            <CommonTable
                columns = {columns}
                data = {withAccountSectionHeadings(returnValue, this.props.langText)}
                className = "none-overflow account-info-table"/>
        );
    }
}

/**
 * 外線番号の名称編集モーダル（Issue #289）
 *
 * 保存は画面下部の「確認」に一本化しているため、ここには保存ボタンを置かない。
 * 入力はその場で親の state（externalNumberDescriptions）へ反映する。
 *
 * props:
 *   show: boolean
 *   onHide: () => void
 *   externalNumbers: [{external_number, description}]
 *   descriptions: {外線番号: 名称}
 *   onChange: (externalNumber, value) => void
 *   langText
 */
/**
 * 再認証（本人確認）用の現在のパスワード入力モーダル。
 *
 * メールアドレス・二要素認証を変更したときはサーバが現在のパスワードを要求する。
 * その入力欄を表へ置くと編集画面にしか無いことになり、確認画面で再認証が
 * 失敗したときに直す先が見えない。保存を実行する「設定」の直前に出して、
 * 失敗したときは閉じずにその場で入れ直せるようにする。
 *
 * props:
 *   show: boolean
 *   description: string  なぜ要るのかの1文（メールアドレス／二要素認証で文言を変える）
 *   password: string
 *   errorMessage: string|null  再認証に失敗したときにモーダル内へ出す
 *   isSaving: boolean
 *   onChangePassword: (value) => void
 *   onSubmit: () => void
 *   onHide: () => void
 *   langText
 */
export class ReauthPasswordModal extends Component {
    render() {
        const {
            show, description, password, errorMessage, isSaving,
            onChangePassword, onSubmit, onHide, langText
        } = this.props;

        return (
            // backdrop="static" で背景クリックでは閉じない。
            // 入力途中に閉じると保存操作ごと取り消しになるため。
            // 保存中は Escape でも閉じさせない（閉じても API は止まらず、
            // どこまで保存されたのか分からない状態になる）。
            <Modal show={show} onHide={onHide} backdrop="static" keyboard={!isSaving}>
                <Modal.Header>
                    <Modal.Title>{langText.Body.AccountReauthModalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{description}</p>
                    {/* パスワード欄は Enter で送信できるのが自然な操作だが、
                        このモーダルは form の外にあるため自前で拾う */}
                    <InlineForm
                        key="reauth-password"
                        controlId="reauth-password"
                        type="password"
                        autoComplete="current-password"
                        label={langText.Body.AccountCurrentPassword}
                        value={password || ""}
                        onChange={e => onChangePassword(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter" && password && !isSaving) onSubmit();
                        }}
                        isInvalid={!!errorMessage}
                    />
                    {errorMessage &&
                        <p className="reauth-modal-error">{errorMessage}</p>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        className="reauth-modal-submit"
                        variant="primary"
                        disabled={!password || isSaving}
                        onClick={() => onSubmit()}
                    >
                        {isSaving ? langText.Body.Wait : langText.Body.Set}
                    </Button>
                    <Button
                        className="reauth-modal-cancel"
                        variant="secondary"
                        disabled={isSaving}
                        onClick={onHide}
                    >
                        {langText.Body.Cancel}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export class ExternalNumberDescriptionModal extends Component {
    render() {
        const {show, onHide, externalNumbers, descriptions, onChange, langText} = this.props;

        return (
            <Modal show={show} onHide={onHide} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{langText.Body.AccountExternalNumberDescriptionTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* 何のための名称なのかを画面に出す。通話履歴・CSV に出る値だと分かれば、
                        「営業部」のような組織名を入れる判断ができる。 */}
                    <p className="external-number-modal-note">
                        {langText.Body.AccountExternalNumberDescriptionNote}
                    </p>
                    <div className="external-number-modal-body">
                        {(externalNumbers || []).map(row => (
                            <InlineForm
                                key={`desc-${row.external_number}`}
                                controlId={`desc-${row.external_number}`}
                                type="text"
                                label={row.external_number}
                                value={descriptions[row.external_number] || ""}
                                placeholder={langText.Body.AccountExternalNumberDescriptionPlaceholder}
                                maxLength={128}
                                onChange={e => onChange(row.external_number, e.target.value)}
                            />
                        ))}
                    </div>
                    <hr />
                    {/* 保存は「確認」で行うことを明示する。ここで閉じても入力値は保持される */}
                    <p className="external-number-modal-note">
                        {langText.Body.AccountExternalNumberDescriptionSaveNote}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        {langText.Body.Close}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}
export class SubUserPermissionModal extends Component {
    constructor(props) {
        super(props);
        this.state = this.getInitialState(props.subUser);
    }

    getInitialState(subUser) {
        const options = subUser ? subUser.options : null;
        return {
            permData: options && options.permission_data
                ? JSON.parse(JSON.stringify(options.permission_data))
                : {},
            externalNumbers: options && options.external_numbers !== undefined
                ? options.external_numbers
                : null,
            // RecAceApp 未設定（新規サブユーザー）の場合は true をデフォルトとする。
            // Sign.js Stage4 の「subRecAceApp !== undefined ? subRecAceApp : true」と同じ方針で
            // 親が RecAceApp=true なら子もデフォルト有効（明示的に false を設定した場合のみ無効）。
            recAceApp: options && options.RecAceApp !== undefined ? options.RecAceApp : true,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.subUser !== this.props.subUser && this.props.subUser) {
            this.setState(this.getInitialState(this.props.subUser));
        }
    }

    setPermission(feature, key, value) {
        let permData = JSON.parse(JSON.stringify(this.state.permData));
        if (!permData[feature]) permData[feature] = {};
        permData[feature][key] = value;
        this.setState({ permData });
    }

    toggleExternalNumber(number) {
        let externalNumbers = this.state.externalNumbers ? [...this.state.externalNumbers] : [];
        if (externalNumbers.includes(number)) {
            externalNumbers = externalNumbers.filter(n => n !== number);
        } else {
            externalNumbers.push(number);
        }
        this.setState({ externalNumbers });
    }

    isPermOn(feature, key) {
        // 【#475】親の hm73 権限が false の権限は、SignPermission.js の天井判定により
        // 会社共通設定（sub_user_permission）や個別設定に true があってもセッションへ反映されない。
        // 表示だけ ON にすると「ON（変更不可）」と出るのに実効は false という不一致になるため、
        // 親が false の時点で OFF を返す。disabled は isParentPermDisabled が別に立てる。
        if (this.isParentPermDisabled(feature, key)) {
            return false;
        }
        const permData = this.state.permData;
        // 個別設定がある場合は優先（明示的なtrue/false）
        if (permData && permData[feature] && permData[feature].hasOwnProperty(key)) {
            return permData[feature][key] !== false;
        }
        // 個別設定がない場合は会社デフォルト（sub_user_permission）を参照して現状を表示
        const companyPerm = this.props.companySubUserPermission;
        if (companyPerm && companyPerm[feature] && companyPerm[feature].hasOwnProperty(key)) {
            return companyPerm[feature][key] !== false;
        }
        return true;
    }

    isParentPermDisabled(feature, key) {
        // 天井は親ユーザーのhm73権限のみ（親が持たない権限は付与不可）
        const parentPerm = this.props.parentPermissionData;
        return !!(parentPerm && parentPerm[feature] && parentPerm[feature][key] === false);
    }

    handleSave() {
        const { permData, externalNumbers, recAceApp } = this.state;
        // FR-03 で ALLOWED_PERMISSION_FEATURES が ['Record'] のみに絞られたため、
        // 旧実装で作成されたサブユーザーが保持する旧キー
        // (PhoneBook/Category/ProperNoun/AlarmSetting/NotificationSetting) を除外する。
        // サーバ側 validateSubUserOptions で旧キーを検出すると 400 InvalidParameter で
        // 弾かれるためフロント側で正規化してから送信する。
        const filteredPermData = permData && permData.Record
            ? { Record: permData.Record }
            : {};
        const options = {
            permission_data: filteredPermData,
            external_numbers: externalNumbers,
        };
        if (this.props.parentOptions && this.props.parentOptions.RecAceApp) {
            options.RecAceApp = recAceApp;
        }
        this.props.onSave(this.props.subUser.id, options);
    }

    render() {
        const {
            show, onHide, subUser,
            parentOptions,
            externalNumbers: availableNumbers,
            langText
        } = this.props;
        const { externalNumbers } = this.state;
        const bodyText = langText.Body;

        if (!subUser) return null;

        return (
            <Modal show={show} onHide={onHide} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{bodyText.AccountSubUserPermission}: {subUser.sub_user_id}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h6>{bodyText.AccountSubUserPermissionRecord}</h6>
                    {/* read（録音一覧の閲覧）はサブユーザーの前提権限のため個別トグルは設けない。
                        制御対象は再生(playback)・ダウンロード(download)の2つに限定する。 */}
                    <Form.Check
                        type="switch"
                        id="perm-playback"
                        label={bodyText.AccountSubUserPermissionPlayback}
                        checked={this.isPermOn('Record', 'playback')}
                        disabled={this.isParentPermDisabled('Record', 'playback')}
                        onChange={e => this.setPermission('Record', 'playback', e.target.checked)}
                    />
                    <Form.Check
                        type="switch"
                        id="perm-download"
                        label={bodyText.AccountSubUserPermissionDownload}
                        checked={this.isPermOn('Record', 'download')}
                        disabled={this.isParentPermDisabled('Record', 'download')}
                        onChange={e => this.setPermission('Record', 'download', e.target.checked)}
                    />

                    {parentOptions && parentOptions.RecAceApp && (
                        <>
                            <hr />
                            <h6>{bodyText.AccountSubUserPermissionMenu}</h6>
                            <Form.Check
                                type="switch"
                                id="perm-RecAceApp"
                                label={bodyText.AccountSubUserPermissionRecAceApp}
                                checked={this.state.recAceApp}
                                onChange={e => this.setState({ recAceApp: e.target.checked })}
                            />
                        </>
                    )}

                    {availableNumbers && availableNumbers.length > 0 && (
                        <>
                            <hr />
                            <h6>{bodyText.AccountSubUserPermissionExtNumber}</h6>
                            <Form.Check
                                type="radio"
                                name="external-mode"
                                id="ext-no-restriction"
                                label={bodyText.AccountSubUserPermissionExtNoRestrict}
                                checked={externalNumbers === null}
                                onChange={() => this.setState({ externalNumbers: null })}
                            />
                            <Form.Check
                                type="radio"
                                name="external-mode"
                                id="ext-restrict"
                                label={bodyText.AccountSubUserPermissionExtSpecify}
                                checked={externalNumbers !== null}
                                onChange={() => this.setState({ externalNumbers: [] })}
                            />
                            {externalNumbers !== null && availableNumbers.map(en => (
                                <Form.Check
                                    key={en.external_number}
                                    type="checkbox"
                                    id={`ext-${en.external_number}`}
                                    label={en.description ? `${en.external_number}（${en.description}）` : en.external_number}
                                    checked={externalNumbers.includes(en.external_number)}
                                    onChange={() => this.toggleExternalNumber(en.external_number)}
                                    style={{ marginLeft: '1.5rem' }}
                                />
                            ))}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => this.handleSave()}>
                        {bodyText.Save}
                    </Button>
                    <Button variant="secondary" onClick={onHide}>
                        {bodyText.Cancel}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export class ViewAccountInfo extends Component {

    subUsersGetView() {
        return this.props.state.subUsers.map((row,index) => {
            return <Row className = "sub_user_row">
                <Col className = "sub_user">
                    <InlineForm
                        key = {`sub_user_id-${index}`}
                        controlId = {`sub_user_id-${index}`}
                        value={row.sub_user_id}
                        readOnly
                    />
                </Col>
                <Col className = "sub_user_memo">
                    <InlineForm
                        key = {`sub_user_memo-${index}`}
                        controlId = {`sub_user_memo-${index}`}
                        type = "text"
                        value = {row.memo}
                        readOnly
                    />
                </Col>
                {this.subUserMfaView(row)}
                <Col className = "sub_user_password">
                    {row.password ? <InlineForm
                            key = {`sub_user_password-${index}`}
                            controlId = {`sub_user_password-${index}`}
                            type = "text"
                            value = {row.password}
                            readOnly/>
                        : <InlineForm
                            key = {`sub_user_password-${index}`}
                            controlId = {`sub_user_password-${index}`}
                            type = "text"
                            value = {this.props.langText.Body.AccountNoChange}
                            readOnly/>
                    }
                </Col>
            </Row>
        });
    }


    /**
     * 確認画面のサブユーザー行に出す二要素認証の列（Issue #443）
     * 編集画面と同じ位置に同じ内容を出す。
     */
    subUserMfaView(row) {
        // 編集画面の subUserMfaCols と同じ条件。サブユーザー自身のセッションでは出さない
        if (this.props.isSubUserSession) return null;

        const mfaRow = (this.props.state.mfaSubUsers || [])
            .find(item => item.sub_user_id === row.sub_user_id);
        if (!mfaRow) {
            return (
                <>
                    <Col className="sub_user_mfa_mail text-muted">
                        {this.props.langText.Body.MfaSettingSubUserAfterSave}
                    </Col>
                    <Col className="sub_user_mfa_switch" />
                </>
            );
        }
        return (
            <>
                <Col className="sub_user_mfa_mail">
                    {mfaRow.mail_address || this.props.langText.Body.MfaSettingSubUserMailUnsetShort}
                </Col>
                <Col className="sub_user_mfa_switch">
                    {mfaRow.mfa_enabled
                        ? this.props.langText.Body.MfaStatusOn
                        : this.props.langText.Body.MfaStatusOff}
                </Col>
            </>
        );
    }

    render() {
        let columns = [
            {
                Header: this.props.langText.Body.Item,
                accessor: 'account_info_item',
                width: this.props.boardWidth.xsmall
            }, {
                Header: this.props.langText.Body.Information,
                accessor: 'account_info_data',
                width: this.props.boardWidth.xlarge
            }
        ]
        let returnValue =[];
        returnValue.push({
            section: "basic",
            account_info_item: this.props.langText.Body.AccountInfoList.user_id,
            account_info_data: <InlineForm
                key="user_id"
                controlId="user_id"
                readOnly
                children={this.props.state.user_id||' '}
            />,
        });
        returnValue.push({
            section: "basic",
            account_info_item: this.props.langText.Body.AccountInfoList.name,
            account_info_data: <InlineForm
                key="user_id"
                controlId="user_id"
                readOnly
            >
                <Row>
                    <Col className = "user_name">
                        <InlineForm
                            key = "family_name"
                            controlId = "family_name"
                            label = {this.props.langText.Body.FamilyName}
                            readOnly
                            value = {this.props.state.family_name||' '}
                        />
                    </Col>
                    <Col className = "user_name">
                        <InlineForm
                            key = "first_name"
                            controlId = "first_name"
                            label = {this.props.langText.Body.FirstName}
                            readOnly
                            value = {this.props.state.first_name||' '}
                        />
                    </Col>
                </Row>
            </InlineForm>,
        });
        returnValue.push({
            section: "basic",
            account_info_item: this.props.langText.Body.AccountInfoList.mail_address,
            account_info_data: <InlineForm
                key = "mail_address"
                type = "mail"
                readOnly
                children = {this.props.state.mail_address||' '}
            />,
        });
        returnValue.push({
            section: "basic",
            account_info_item: this.props.langText.Body.AccountInfoList.tel_number,
            account_info_data: <InlineForm
                key = "tel_number"
                type = "text"
                readOnly
                children = {this.props.state.tel_number||' '}
            />,
        });
        returnValue.push(
            {
                section: "security",
                account_info_item: this.props.langText.Body.AccountInfoList.password_change,
                account_info_data: <InlineForm
                    key = "password"
                    controlId = "password"
                    readOnly
                    children = {this.props.state.password_new ? this.props.langText.Body.True : this.props.langText.Body.False}
                />,
            }
        );
        returnValue.push({
            section: "sub_user",
            account_info_item: this.props.langText.Body.AccountInfoList.sub_users,
            account_info_data: <InlineForm
                key="sub_user_id"
                controlId="sub_user_id"
                readOnly
            >
                {/* 確認画面には削除・権限のボタンが無いため、編集画面の見出しにある
                    ボタン分の余白（sub_user_minus-button-space）は置かない。
                    列は行と同じ サブユーザーID／メモ／送信先メール／二要素認証／パスワード の5つ。 */}
                {this.props.state.subUsers.length > 0 ? <Row className="sub_user_row_head">
                    <Col className="sub_user">
                        {this.props.langText.Body.AccountSubUserId}
                    </Col>
                    <Col className="sub_user_memo">
                        {this.props.langText.Body.AccountMemo}
                    </Col>
                    {/* 行側（subUserMfaView）と出す条件を揃える */}
                    {!this.props.isSubUserSession && <>
                        <Col className="sub_user_mfa_mail">
                            {this.props.langText.Body.MfaSettingSubUserMail}
                        </Col>
                        <Col className="sub_user_mfa_switch">
                            {this.props.langText.Body.MfaStatus}
                        </Col>
                    </>}
                    <Col className="sub_user_password">
                        {this.props.langText.Body.Password}
                    </Col>
                </Row> : this.props.langText.Body.AccountNoSubUsers}
                {this.subUsersGetView()}
            </InlineForm>,
        });

        // 録音保存期限の表示
        if (this.props.state.voice_expire_display && this.props.state.voice_expire_display.show) {
            const voiceExpireData = this.props.state.voice_expire_display;
            const expireText = voiceExpireData.option_years > 0
                ? `${voiceExpireData.default_years} ${this.props.langText.Body.Year} + ${voiceExpireData.option_years} ${this.props.langText.Body.Year}`
                : `${voiceExpireData.default_years}  ${this.props.langText.Body.Year}`;

            returnValue.push({
                section: "recording",
                account_info_item: this.props.langText.Body.AccountInfoList.voice_expire,
                account_info_data: <InlineForm
                    key="voice_expire"
                    controlId="voice_expire"
                    readOnly
                    children={expireText}
                />
            });
        }

        // 外線番号の名称（Issue #289）。編集画面と同じまとめ方で1行に出す
        if ((this.props.externalNumbers || []).length > 0) {
            returnValue.push({
                section: "external_number",
                account_info_item: this.props.langText.Body.AccountExternalNumberDescriptionTitle,
                account_info_data: <div key="external-number-block" className="account-sub-block">
                    <Row className="account-sub-block-header">
                        <Col md={4}>{this.props.langText.Body.AccountExternalNumberNumber}</Col>
                        <Col md={8}>{this.props.langText.Body.AccountExternalNumberDescriptionLabel}</Col>
                    </Row>
                    {this.props.externalNumbers.map(row => (
                        <Row className="account-sub-block-row" key={`desc-row-${row.external_number}`}>
                            <Col md={4} className="account-sub-block-label">{row.external_number}</Col>
                            <Col md={8}>
                                {this.props.state.externalNumberDescriptions[row.external_number]
                                    || this.props.langText.Body.AccountNotSet}
                            </Col>
                        </Row>
                    ))}
                </div>
            });
        }

        // 二要素認証（Issue #443）。サブユーザーの分はサブユーザー欄に並べている
        returnValue.push({
            section: "security",
            account_info_item: this.props.langText.Body.MfaStatus,
            account_info_data: <InlineForm
                key = "mfa-enabled"
                controlId = "mfa-enabled"
                readOnly
                children = {this.props.state.mfaEnabled
                    ? this.props.langText.Body.MfaStatusOn
                    : this.props.langText.Body.MfaStatusOff}
            />
        });

        return (
            <CommonTable
                columns = {columns}
                data = {withAccountSectionHeadings(returnValue, this.props.langText)}
                className = "none-overflow account-info-table"
                loading={this.props.state.loading}/>
        );
    }
}

